/** biome-ignore-all lint/suspicious/noArrayIndexKey: <explanation> */
/** biome-ignore-all lint/style/noMagicNumbers: <explanation> */
"use client";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import ArrowIcon from "../icons/arrow";

const generatePages = (currentPage: number, totalPages: number) => {
  // Toplam sayfa sayısı 7 veya daha az ise, tüm sayfaları göster
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // Mevcut sayfa başlangıca yakınsa
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }

  // Mevcut sayfa sona yakınsa
  if (currentPage >= totalPages - 3) {
    return [
      1,
      "...",
      totalPages - 4,
      totalPages - 3,
      totalPages - 2,
      totalPages - 1,
      totalPages,
    ];
  }

  // Mevcut sayfa ortalardaysa
  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    totalPages,
  ];
};

const PageButton = ({
  number,
  isActive,
  href,
}: {
  number: number;
  isActive?: boolean;
  href: string;
}) => {
  return (
    <Link
      className={cn(
        "font-bold text-center inline-flex justify-center items-center hover:bg-brand-blue-200 rounded-full",
        "size-8 text-xs cursor-pointer leading-4.5",
        "md:size-10 xl:size-12 2xl:size-14",
        isActive && "bg-brand-blue-500 text-white hover:bg-brand-blue-500",
      )}
      href={href}
    >
      {number}
    </Link>
  );
};

interface PaginationProps {
  totalPages: number;
  currentPage?: number;
}

const Pagination = ({ totalPages, currentPage = 1 }: PaginationProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const createPageUrl = (pageNumber: number | string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", pageNumber.toString());
    return `${pathname}?${params.toString()}`;
  };

  const pages = useMemo(
    () => generatePages(currentPage, totalPages),
    [currentPage, totalPages],
  );

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="flex flex-row w-fit mx-auto rounded-full bg-neutral-100 gap-1 justify-center items-center p-1">
      {currentPage > 1 ? (
        <Link
          className={cn(
            "font-bold text-center inline-flex justify-center items-center hover:bg-brand-blue-200 rounded-full",
            "size-8 text-xs cursor-pointer leading-4.5",
            "md:size-10 xl:size-12 2xl:size-14",
          )}
          href={createPageUrl(currentPage - 1)}
        >
          <ArrowIcon className="stroke-brand-blue-600 rotate-180" />
        </Link>
      ) : (
        <button
          className={cn(
            "font-bold text-center inline-flex justify-center items-center disabled:cursor-not-allowed disabled:opacity-50 hover:bg-brand-blue-200 rounded-full",
            "size-8 text-xs cursor-pointer leading-4.5",
            "md:size-10 xl:size-12 2xl:size-14",
          )}
          disabled={true}
          type="button"
        >
          <ArrowIcon className="stroke-brand-blue-600 rotate-180" />
        </button>
      )}

      {pages.map((page, index) => {
        if (typeof page === "string") {
          return (
            <span
              className={cn(
                "font-bold text-center inline-flex justify-center items-center text-neutral-400",
                "size-8 text-xs leading-4.5",
                "md:size-10 xl:size-12 2xl:size-14",
              )}
              key={`ellipsis-${index}`}
            >
              ...
            </span>
          );
        }
        return (
          <PageButton
            href={createPageUrl(page)}
            isActive={currentPage === page}
            key={page}
            number={page}
          />
        );
      })}

      {currentPage < totalPages ? (
        <Link
          className={cn(
            "font-bold text-center inline-flex justify-center items-center hover:bg-brand-blue-200 rounded-full",
            "size-8 text-xs cursor-pointer leading-4.5",
            "md:size-10 xl:size-12 2xl:size-14",
          )}
          href={createPageUrl(currentPage + 1)}
        >
          <ArrowIcon className="stroke-brand-blue-600" />
        </Link>
      ) : (
        <button
          className={cn(
            "font-bold text-center inline-flex justify-center items-center disabled:cursor-not-allowed disabled:opacity-50 hover:bg-brand-blue-200 rounded-full",
            "size-8 text-xs cursor-pointer leading-4.5",
            "md:size-10 xl:size-12 2xl:size-14",
          )}
          disabled={true}
          type="button"
        >
          <ArrowIcon className="stroke-brand-blue-600" />
        </button>
      )}
    </div>
  );
};

export default Pagination;
